import React, { useState, useEffect, useMemo } from "react";
import { HiDesktopComputer, HiCube } from "react-icons/hi";
import { getImageIcon } from "../../utils/iconRegistry";
import appIcons from "../../utils/appIcons.json";
import osWithPanel from "../../utils/osWithPanel.json";
import { url } from "../../utils/pubUrl";
import "./OrderImageSection.css";

const OSIcon = ({ distribution }) => {
  const [state, setState] = useState({
    type: "icon",
    Icon: null,
    src: null,
    label: "",
  });

  useEffect(() => {
    let mounted = true;
    (async () => {
      // Extract base slug (e.g., "ubuntu-20-04" -> "ubuntu")
      const baseSlug = distribution?.slug?.split("-")[0] || distribution;
      const res = await getImageIcon({
        slug: baseSlug.toLowerCase(),
        name: baseSlug,
      });
      if (mounted) setState(res);
    })();
    return () => (mounted = false);
  }, [distribution]);

  if (state.type === "img" && state.src) {
    return <img src={state.src} alt={state.label} className="os-icon-image" />;
  }
  const I = state.Icon;
  return I ? (
    <I className="os-icon-fallback" />
  ) : (
    <HiDesktopComputer className="os-icon-fallback" />
  );
};

function normalizeAppKey(app) {
  const name = String(app?.name || "").toLowerCase();
  const slug = String(app?.slug || "").toLowerCase();

  // Prefer slug first token (DigitalOcean marketplace slugs usually start with product name)
  const slugBase = slug.split("-")[0];

  // Name before " on " (e.g. "n8n 1.110.1 on Ubuntu 24.04" -> "n8n 1.110.1")
  const nameLeft = name.split(" on ")[0];

  // Normalize: remove parentheses, version numbers, extra words; keep hyphens for keys like uptime-kuma
  const cleaned = nameLeft
    .replace(/\(.*?\)/g, " ")
    .replace(/\b\d+(\.\d+){0,3}\b/g, " ")
    .replace(/[^a-z0-9\s-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const tokens = cleaned.split(/[\s-]+/).filter(Boolean);
  const firstToken = tokens[0] || "";

  // Special-case combos where we want a primary icon
  if (cleaned.includes("wordpress")) return "wordpress";
  if (cleaned.includes("woocommerce")) return "woocommerce";

  // Prefer exact key matches from our json (try progressively)
  const candidates = [
    slug, // full slug
    slugBase, // first token
    cleaned.replace(/\s+/g, "-"), // full cleaned as kebab
    firstToken, // first token
  ].filter(Boolean);

  for (const c of candidates) {
    if (appIcons[c]) return c;
  }

  // If user has a hyphenated key like "n8n-queue-mode", try to find it within cleaned/slug
  for (const key of Object.keys(appIcons)) {
    if (slug.includes(key) || cleaned.replace(/\s+/g, "-").includes(key)) {
      return key;
    }
  }

  return null;
}

// Check if an application has an icon in appIcons.json
const hasAppIcon = (app) => {
  const appKey = normalizeAppKey(app);
  return appKey ? !!appIcons[appKey] : false;
};

const AppIcon = ({ app }) => {
  const appKey = useMemo(() => normalizeAppKey(app), [app]);
  const iconFile = appKey ? appIcons[appKey] : null;

  // Prefer user-provided icon map from /public/application-icon/*
  if (iconFile) {
    const iconSrc = url(`/application-icon/${iconFile}`);
    return (
      <img
        src={iconSrc}
        alt={app?.name || app?.slug || "app"}
        className="app-icon-image"
        loading="lazy"
      />
    );
  }

  // No mapping found: show a default application icon (do not request local /icons/* assets)
  return <HiCube className="os-icon-fallback" />;
};

const OSWithPanelIcon = ({ app }) => {
  const appKey = useMemo(() => normalizeAppKey(app), [app]);
  const panelKeys = Object.keys(osWithPanel);

  // Find matching panel key
  const matchingPanelKey = panelKeys.find((panelKey) => {
    if (!appKey) return false;
    const appKeyLower = appKey.toLowerCase();
    const panelKeyLower = panelKey.toLowerCase();
    return (
      appKeyLower === panelKeyLower ||
      appKeyLower.includes(panelKeyLower) ||
      app.slug?.toLowerCase().includes(panelKeyLower) ||
      app.name?.toLowerCase().includes(panelKeyLower)
    );
  });

  const iconFile = matchingPanelKey ? osWithPanel[matchingPanelKey] : null;

  // Use OS with Panel icon if found
  if (iconFile) {
    const iconSrc = url(`/application-icon/${iconFile}`);
    return (
      <img
        src={iconSrc}
        alt={app?.name || app?.slug || "panel"}
        className="app-icon-image"
        loading="lazy"
      />
    );
  }

  // Fallback to AppIcon if no panel icon found
  return <AppIcon app={app} />;
};

const OrderImageSection = ({
  imageGroups,
  handleOSSelect,
  selectedOS,
  availableVersions,
  handleVersionSelect,
  formData,
  handleApplicationSelect,
  applications,
  distributions,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("plain-os"); // plain-os, os-with-panel, application
  const [currentPage, setCurrentPage] = useState(1); // Pagination: current page number
  const itemsPerPage = 20;

  // Categorize distributions
  const plainOSList = Object.keys(imageGroups).map((osFamily) => ({
    family: osFamily,
    items: imageGroups[osFamily],
  }));

  // Get OS with Panel applications (filtered by osWithPanel.json keys)
  const getOSWithPanelApplications = useMemo(() => {
    const panelKeys = Object.keys(osWithPanel);
    return applications.filter((app) => {
      const appKey = normalizeAppKey(app);
      if (!appKey) return false;

      // Check if app key matches any panel key
      return panelKeys.some((panelKey) => {
        const appKeyLower = appKey.toLowerCase();
        const panelKeyLower = panelKey.toLowerCase();
        // Exact match or contains the panel key
        return (
          appKeyLower === panelKeyLower ||
          appKeyLower.includes(panelKeyLower) ||
          app.slug?.toLowerCase().includes(panelKeyLower) ||
          app.name?.toLowerCase().includes(panelKeyLower)
        );
      });
    });
  }, [applications]);

  // Filter ALL applications based on search (across all, not just visible)
  // Sort so applications with icons appear first
  const getAllFilteredApplications = () => {
    const query = searchQuery.toLowerCase();
    let filtered = applications;

    if (query) {
      filtered = applications.filter(
        (app) =>
          (app.name || app.slug).toLowerCase().includes(query) ||
          app.description?.toLowerCase().includes(query)
      );
    }

    // Sort: applications with icons first, then those without
    return [...filtered].sort((a, b) => {
      const aHasIcon = hasAppIcon(a);
      const bHasIcon = hasAppIcon(b);

      if (aHasIcon && !bHasIcon) return -1; // a comes first
      if (!aHasIcon && bHasIcon) return 1; // b comes first
      return 0; // keep original order if both have or both don't have icons
    });
  };

  // Filter OS with Panel applications based on search
  const getFilteredOSWithPanel = () => {
    const query = searchQuery.toLowerCase();
    if (!query) {
      return getOSWithPanelApplications;
    }
    return getOSWithPanelApplications.filter(
      (app) =>
        (app.name || app.slug).toLowerCase().includes(query) ||
        app.description?.toLowerCase().includes(query)
    );
  };

  // Filter items based on search and active tab
  const getFilteredItems = () => {
    const query = searchQuery.toLowerCase();

    if (activeTab === "plain-os") {
      return plainOSList.filter((group) =>
        group.family.toLowerCase().includes(query)
      );
    } else if (activeTab === "os-with-panel") {
      // Filter OS with Panel applications
      return getFilteredOSWithPanel();
    } else if (activeTab === "application") {
      // Search across ALL applications, then paginate
      return getAllFilteredApplications();
    }

    return [];
  };

  const filteredItems = getFilteredItems();

  // Calculate pagination for applications and OS with Panel
  const totalPages = Math.ceil(
    activeTab === "application" || activeTab === "os-with-panel"
      ? filteredItems.length / itemsPerPage
      : 1
  );

  // Get paginated applications (for application and os-with-panel tabs)
  const getPaginatedApplications = () => {
    if (activeTab !== "application" && activeTab !== "os-with-panel") return [];
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredItems.slice(startIndex, endIndex);
  };

  const paginatedApps = getPaginatedApplications();

  // Reset to page 1 when tab or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchQuery]);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5; // Show max 5 page numbers at a time

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show pages with ellipsis
      if (currentPage <= 3) {
        // Near the start
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Near the end
        pages.push(1);
        pages.push("ellipsis");
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // In the middle
        pages.push(1);
        pages.push("ellipsis");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Handle selection
  const handleItemSelect = (item) => {
    if (activeTab === "plain-os") {
      handleOSSelect(item.family);
    } else if (activeTab === "application" || activeTab === "os-with-panel") {
      handleApplicationSelect(item.slug);
    }
  };

  // Check if an item is selected
  const isItemSelected = (item) => {
    if (activeTab === "plain-os") {
      return selectedOS === item.family;
    } else if (activeTab === "application" || activeTab === "os-with-panel") {
      return formData.image === item.slug;
    }
    return false;
  };

  return (
    <div className="image-selector-wrapper">
      {/* Search Bar */}
      <div className="image-search-container">
        <svg
          className="search-icon"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
        >
          <path
            d="M9 16C12.866 16 16 12.866 16 9C16 5.13401 12.866 2 9 2C5.13401 2 2 5.13401 2 9C2 12.866 5.13401 16 9 16Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M18 18L14 14"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <input
          type="text"
          className="image-search-input"
          placeholder={
            activeTab === "application"
              ? "Search Application"
              : activeTab === "os-with-panel"
              ? "Search OS with Panel"
              : "Search OS"
          }
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Tab Pills */}
      <div className="image-tabs">
        <button
          type="button"
          className={`image-tab ${activeTab === "plain-os" ? "active" : ""}`}
          onClick={() => {
            setActiveTab("plain-os");
            setSearchQuery("");
          }}
        >
          Plain OS
        </button>
        <button
          type="button"
          className={`image-tab ${
            activeTab === "os-with-panel" ? "active" : ""
          }`}
          onClick={() => {
            setActiveTab("os-with-panel");
            setSearchQuery("");
          }}
        >
          OS With Panel
        </button>
        <button
          type="button"
          className={`image-tab ${activeTab === "application" ? "active" : ""}`}
          onClick={() => {
            setActiveTab("application");
            setSearchQuery("");
          }}
        >
          Application
        </button>
      </div>

      {/* Items Grid */}
      <div className="image-items-grid">
        {activeTab === "plain-os" &&
          filteredItems.map((group) => (
            <div
              key={group.family}
              className={`image-item-card ${
                isItemSelected(group) ? "selected" : ""
              }`}
              onClick={() => handleItemSelect(group)}
            >
              <div className="image-item-icon">
                <OSIcon distribution={group.items[0]} />
              </div>
              <div className="image-item-name">{group.family}</div>
              {/* Optional star for favorites */}
              {/* <svg className="image-item-star" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
              </svg> */}
            </div>
          ))}

        {activeTab === "os-with-panel" &&
          (paginatedApps.length > 0 ? (
            paginatedApps.map((app) => (
              <div
                key={app.slug}
                className={`image-item-card ${
                  isItemSelected(app) ? "selected" : ""
                }`}
                onClick={() => handleItemSelect(app)}
              >
                <div className="image-item-icon">
                  <OSWithPanelIcon app={app} />
                </div>
                <div className="image-item-name">{app.name}</div>
              </div>
            ))
          ) : (
            <div className="image-empty-state">
              <p>
                {searchQuery
                  ? `No results found for "${searchQuery}"`
                  : "No OS with Panel available"}
              </p>
            </div>
          ))}

        {activeTab === "application" &&
          (paginatedApps.length > 0 ? (
            paginatedApps.map((app) => (
              <div
                key={app.slug}
                className={`image-item-card ${
                  isItemSelected(app) ? "selected" : ""
                }`}
                onClick={() => handleItemSelect(app)}
              >
                <div className="image-item-icon">
                  <AppIcon app={app} />
                </div>
                <div className="image-item-name">{app.name}</div>
              </div>
            ))
          ) : (
            <div className="image-empty-state">
              <p>
                {searchQuery
                  ? `No results found for "${searchQuery}"`
                  : "No applications available"}
              </p>
            </div>
          ))}

        {activeTab === "plain-os" && filteredItems.length === 0 && (
          <div className="image-empty-state">
            <p>No results found for "{searchQuery}"</p>
          </div>
        )}
      </div>

      {/* Version Selection - Only show when a Plain OS is selected */}
      {activeTab === "plain-os" &&
        selectedOS &&
        availableVersions.length > 0 && (
          <div className="version-selection-section">
            <label className="version-label">Select Version</label>
            <div className="version-dropdown-wrapper">
              <select
                className="version-select"
                value={formData.imageVersion || ""}
                onChange={(e) => {
                  const version = availableVersions.find(
                    (v) => (v.slug || v.id) === e.target.value
                  );
                  if (version) handleVersionSelect(version);
                }}
                required
              >
                {availableVersions.map((version) => (
                  <option
                    key={version.slug || version.id}
                    value={version.slug || version.id}
                  >
                    {version.name || version.slug}
                  </option>
                ))}
              </select>
              <svg
                className="version-dropdown-arrow"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M5 7.5L10 12.5L15 7.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        )}

      {/* Pagination - For Application and OS With Panel tabs */}
      {(activeTab === "application" || activeTab === "os-with-panel") &&
        totalPages > 1 && (
          <div className="pagination-container">
            <button
              type="button"
              className="pagination-btn pagination-prev"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ display: "block" }}
              >
                <path
                  d="M10 12L6 8L10 4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            <div className="pagination-numbers">
              {getPageNumbers().map((page, index) => {
                if (page === "ellipsis") {
                  return (
                    <span
                      key={`ellipsis-${index}`}
                      className="pagination-ellipsis"
                    >
                      ...
                    </span>
                  );
                }
                return (
                  <button
                    key={page}
                    type="button"
                    className={`pagination-number ${
                      currentPage === page ? "active" : ""
                    }`}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              className="pagination-btn pagination-next"
              onClick={() =>
                setCurrentPage((prev) => Math.min(totalPages, prev + 1))
              }
              disabled={currentPage === totalPages}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ display: "block" }}
              >
                <path
                  d="M6 4L10 8L6 12"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        )}
    </div>
  );
};

export default OrderImageSection;
